import { defineStore } from "pinia";
import { conversationsService } from "@/serviceHandlers/conversationsService";
import { useLoginStore } from "@/stores/loginStore";

export const useMessagesStore = defineStore("messages", {
  state: () => ({
    conversations: [],
    activeConversation: null,
    messages: [],
    unreadCount: 0,
    loading: false,
    messagesLoading: false,
    hasMoreMessages: true,
    lastUpdate: null,
    pollingInterval: null,
    messagePollingInterval: null,
    _unreadDividerPosition: null, // { conversationId, messageId } - internal
  }),

  getters: {
    // Expose unread divider position as a getter for proper Vue reactivity
    unreadDividerPosition: (state) => state._unreadDividerPosition,

    // Get the other participant in a direct conversation
    getDirectParticipant: (state) => {
      if (!state.activeConversation || state.activeConversation.type !== "direct") {
        return null;
      }
      const loginStore = useLoginStore();
      const currentUserId = loginStore.user?.id;
      return state.activeConversation.participants?.find(
        (p) => p.user?.id !== currentUserId
      )?.user;
    },

    // Get conversation display name
    getConversationDisplayName: (state) => (conversation) => {
      if (!conversation) return "";
      if (conversation.type === "group") {
        return conversation.name || "Group Conversation";
      }
      // For direct conversations, show the other user's name
      const loginStore = useLoginStore();
      const currentUserId = loginStore.user?.id;
      const otherParticipant = conversation.participants?.find(
        (p) => p.user?.id !== currentUserId
      )?.user;
      if (otherParticipant) {
        return `${otherParticipant.first_name} ${otherParticipant.last_name}`;
      }
      return "Direct Message";
    },

    // Get sorted conversations (most recent first)
    sortedConversations: (state) => {
      return [...state.conversations].sort((a, b) => {
        const dateA = a.last_message_at ? new Date(a.last_message_at) : new Date(0);
        const dateB = b.last_message_at ? new Date(b.last_message_at) : new Date(0);
        return dateB - dateA;
      });
    },

    // Get conversations with unread messages
    unreadConversations: (state) => {
      return state.conversations.filter((c) => c.unreadCount > 0);
    },
  },

  actions: {
    // Load all conversations for the current user
    async loadConversations() {
      this.loading = true;
      try {
        const conversations = await conversationsService.list();
        this.conversations = conversations;
        this.lastUpdate = new Date();
      } catch {
      } finally {
        this.loading = false;
      }
    },

    // Load unread count
    async loadUnreadCount() {
      try {
        const response = await conversationsService.getUnreadCount();
        this.unreadCount = response.unreadCount || 0;
      } catch {
      }
    },

    // Set active conversation and load messages
    async setActiveConversation(conversationId) {
      if (!conversationId) {
        this.activeConversation = null;
        this.messages = [];
        this.hasMoreMessages = true;
        this.stopMessagePolling();
        return;
      }

      this.messagesLoading = true;
      try {
        // Get conversation details
        const conversation = await conversationsService.getById(conversationId);
        this.activeConversation = conversation;

        // Load initial messages
        await this.loadMessages(conversationId);

        // Capture first unread message ID before marking as read
        // Store both conversation ID and message ID to prevent flicker on switch
        // Use activeConversation.id to ensure type consistency with MessageThread's comparison
        const firstUnread = this.messages.find(m => !m.isRead);
        this._unreadDividerPosition = firstUnread
          ? { conversationId: this.activeConversation.id, messageId: firstUnread.id }
          : null;

        // Mark conversation as read
        await this.markConversationAsRead(conversationId);

        // Start polling for new messages
        this.startMessagePolling();
      } catch {
      } finally {
        this.messagesLoading = false;
      }
    },

    // Load messages for a conversation
    async loadMessages(conversationId, before = null) {
      try {
        const params = { limit: 50 };
        if (before) {
          params.before = before;
        }
        const response = await conversationsService.getMessages(conversationId, params);

        if (before) {
          // Prepend older messages
          this.messages = [...response.messages, ...this.messages];
        } else {
          this.messages = response.messages;
        }

        this.hasMoreMessages = response.pagination.hasMore;
      } catch {
      }
    },

    // Load more messages (infinite scroll)
    async loadMoreMessages() {
      if (!this.activeConversation || !this.hasMoreMessages || this.messagesLoading) {
        return;
      }

      const firstMessage = this.messages[0];
      if (!firstMessage) return;

      this.messagesLoading = true;
      try {
        await this.loadMessages(this.activeConversation.id, firstMessage.id);
      } finally {
        this.messagesLoading = false;
      }
    },

    // Send a message
    async sendMessage(content, files = []) {
      if (!this.activeConversation) return null;

      try {
        let message;
        if (files.length > 0) {
          message = await conversationsService.sendMessageWithAttachments(
            this.activeConversation.id,
            content,
            files
          );
        } else {
          message = await conversationsService.sendMessage(
            this.activeConversation.id,
            { content }
          );
        }
        this.messages.push(message);

        // Update conversation's last message
        const convIndex = this.conversations.findIndex(
          (c) => c.id === this.activeConversation.id
        );
        if (convIndex !== -1) {
          this.conversations[convIndex].lastMessage = message;
          this.conversations[convIndex].last_message_at = message.createdAt;
        }

        return message;
      } catch (error) {
        throw error;
      }
    },

    // Edit a message
    async editMessage(messageId, content) {
      try {
        const updatedMessage = await conversationsService.editMessage(messageId, {
          content,
        });

        // Update the message in the local state
        const index = this.messages.findIndex((m) => m.id === messageId);
        if (index !== -1) {
          this.messages[index] = {
            ...this.messages[index],
            content: updatedMessage.content,
            edited_at: updatedMessage.edited_at,
          };
        }

        return updatedMessage;
      } catch (error) {
        throw error;
      }
    },

    // Create a new conversation
    async createConversation({ name, type, participantIds }) {
      try {
        const conversation = await conversationsService.create({
          name,
          type,
          participantIds,
        });

        // Add to conversations list if not already present
        const existing = this.conversations.find((c) => c.id === conversation.id);
        if (!existing) {
          this.conversations.unshift({
            ...conversation,
            unreadCount: 0,
            lastMessage: null,
          });
        }

        return conversation;
      } catch (error) {
        throw error;
      }
    },

    // Get or create a direct conversation
    async getOrCreateDirectConversation(userId) {
      try {
        const conversation = await conversationsService.getOrCreateDirect(userId);

        // Add to conversations list if not already present
        const existing = this.conversations.find((c) => c.id === conversation.id);
        if (!existing) {
          this.conversations.unshift({
            ...conversation,
            unreadCount: 0,
            lastMessage: null,
          });
        }

        return conversation;
      } catch (error) {
        throw error;
      }
    },

    // Mark conversation as read
    async markConversationAsRead(conversationId) {
      try {
        await conversationsService.markAsRead(conversationId);

        // Update local state
        const conv = this.conversations.find((c) => c.id === conversationId);
        if (conv) {
          this.unreadCount -= conv.unreadCount || 0;
          conv.unreadCount = 0;
        }

        // Update messages
        this.messages = this.messages.map((m) => ({
          ...m,
          isRead: true,
        }));
      } catch {
      }
    },

    // Leave a conversation
    async leaveConversation(conversationId) {
      try {
        await conversationsService.delete(conversationId);

        // Remove from local state
        this.conversations = this.conversations.filter((c) => c.id !== conversationId);

        if (this.activeConversation?.id === conversationId) {
          this.activeConversation = null;
          this.messages = [];
        }
      } catch (error) {
        throw error;
      }
    },

    // Add participants to a group conversation
    async addParticipants(conversationId, participantIds) {
      try {
        const conversation = await conversationsService.addParticipants(
          conversationId,
          { participantIds }
        );

        // Update local state
        const index = this.conversations.findIndex((c) => c.id === conversationId);
        if (index !== -1) {
          this.conversations[index] = {
            ...this.conversations[index],
            ...conversation,
          };
        }

        if (this.activeConversation?.id === conversationId) {
          this.activeConversation = conversation;
        }

        return conversation;
      } catch (error) {
        throw error;
      }
    },

    // Remove a participant from a group conversation
    async removeParticipant(conversationId, userId) {
      try {
        await conversationsService.removeParticipant(conversationId, userId);

        // Update local state
        if (this.activeConversation?.id === conversationId) {
          this.activeConversation.participants =
            this.activeConversation.participants.filter(
              (p) => p.user?.id !== userId
            );

          // Reload messages to show the system message
          await this.loadMessages(conversationId);
        }
      } catch (error) {
        throw error;
      }
    },

    // Update conversation (name for groups)
    async updateConversation(conversationId, updates) {
      try {
        const conversation = await conversationsService.update(
          conversationId,
          updates
        );

        // Update local state
        const index = this.conversations.findIndex((c) => c.id === conversationId);
        if (index !== -1) {
          this.conversations[index] = {
            ...this.conversations[index],
            ...conversation,
          };
        }

        if (this.activeConversation?.id === conversationId) {
          this.activeConversation = {
            ...this.activeConversation,
            ...conversation,
          };
        }

        return conversation;
      } catch (error) {
        throw error;
      }
    },

    // Poll for new messages in active conversation
    async pollNewMessages() {
      if (!this.activeConversation) return;

      try {
        const response = await conversationsService.getMessages(
          this.activeConversation.id,
          { limit: 50 }
        );

        // Check for new messages
        const currentIds = new Set(this.messages.map((m) => m.id));
        const newMessages = response.messages.filter((m) => !currentIds.has(m.id));

        if (newMessages.length > 0) {
          this.messages = [...this.messages, ...newMessages];

          // Mark as read since user is viewing
          await this.markConversationAsRead(this.activeConversation.id);
        }
      } catch {
      }
    },

    // Start polling for new messages (10 second interval)
    startMessagePolling() {
      this.stopMessagePolling();
      this.messagePollingInterval = setInterval(() => {
        this.pollNewMessages();
      }, 10000);
    },

    // Stop polling for new messages
    stopMessagePolling() {
      if (this.messagePollingInterval) {
        clearInterval(this.messagePollingInterval);
        this.messagePollingInterval = null;
      }
    },

    // Start polling for conversation list (30 second interval)
    startConversationPolling() {
      this.stopConversationPolling();
      this.pollingInterval = setInterval(() => {
        this.loadConversations();
        this.loadUnreadCount();
      }, 30000);
    },

    // Stop polling for conversation list
    stopConversationPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    // Clean up when leaving messages page
    cleanup() {
      this.stopMessagePolling();
      this.stopConversationPolling();
      this.activeConversation = null;
      this.messages = [];
      this._unreadDividerPosition = null;
    },
  },
});

export default useMessagesStore;
